import { useRef, useState, useEffect } from 'react';
import { Box, Flex, Image } from '@chakra-ui/react';
import { Heading, HStack, VStack, Text } from 'components/ui';
import { Check } from 'lucide-react';

const aybleLogo = '/FA__ayble_health_Logo_Isabelline.png';

const reviews = [
  {
    signature: 'Julia R., IBS',
    review: [
      { text: '“', bold: false },
      { text: 'My Ayble care team changed my life', bold: true },
      {
        text: ' —helping me identify trigger foods with the easiest food logging ever. My bloating improved fast, and they coordinated with my local doctor for seamless care.”',
        bold: false,
      },
    ],
  },
  {
    signature: 'Sam K., Heartburn',
    review: [
      {
        text: '“After years of frustrating gut symptoms and so much trial and error with no good answers, it’s not an overstatement to say Ayble changed my life. ',
        bold: false,
      },
      {
        text: 'It’s the best GI care I’ve ever received. 10/10 would recommend.',
        bold: true,
      },
      { text: '”', bold: false },
    ],
  },
  {
    signature: 'Martin L., Bloating',
    review: [
      {
        text: '“The main thing I’m taking away from Ayble is that there are things I’ve never considered before, like meal size, food texture, and the different types of fiber. I’ve never gotten guidance from my regular doctor — ',
        bold: false,
      },
      {
        text: 'I wish I had found Ayble’s nutrition coaching 5 years ago.',
        bold: true,
      },
      { text: '“', bold: false },
    ],
  },
];

const ReviewCard = ({ review, signature }) => (
  <Box
    backgroundColor="white"
    rounded="lg"
    mx={'auto'}
    w={{ base: '90%', lg: '33%' }}
    h={{ base: '300px', lg: 'auto' }}
    p={6}
    borderColor="#E7E7E7"
    borderWidth={1}
  >
    <VStack gap={6} h={'100%'}>
      <Image src="/five_stars.png" w="92px" h="16px" alt="stars" />
      <Text flex={1} fontSize={'16px'}>
        {review.map((part, index) => (
          <Text key={index} fontWeight={part.bold ? 'bold' : 'normal'}>
            {part.text}
          </Text>
        ))}
      </Text>
      <HStack alignItems={'center'} justifyContent={'space-between'} mt={6}>
        <VStack>
          <Text fontWeight="medium">{signature}</Text>
          <HStack gap={1} alignItems={'center'}>
            <Check color={'#718F9A'} size={16} />
            <Text color={'#718F9A'}>Verified Review</Text>
          </HStack>
        </VStack>
        <Image
          src={aybleLogo}
          w={'60px'}
          h={'35px'}
          objectFit="contain"
          alt="logo"
        />
      </HStack>
    </VStack>
  </Box>
);

export const Reviews = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLElement | null>(null);
  const startXRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      startXRef.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      if (startXRef.current == null) return;
      const endX = e.changedTouches[0].clientX;
      const diff = startXRef.current - endX;

      if (diff > 50) {
        setActiveIndex((prev) => (prev + 1) % reviews.length);
      } else if (diff < -50) {
        setActiveIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
      }
      startXRef.current = null;
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  return (
    <Box
      maxW={{ base: '100%', lg: '1200px' }}
      w={{ base: '100%', lg: '80%' }}
      mx="auto"
      mt={10}
    >
      <Flex
        direction={{ base: 'column', lg: 'row' }}
        alignItems={{ base: '', lg: 'flex-end' }}
        justifyContent="space-between"
        w={{ base: '80%', lg: '100%' }}
        mx={'auto'}
      >
        <Heading
          fontSize={'3xl'}
          w={{ base: '70%', lg: '100%' }}
          color={'secondary.500'}
          mb={{ base: 0, lg: 8 }}
          textAlign={'left'}
          fontWeight={400}
        >
          Don't just take our word for it...
        </Heading>
        <Image
          alignSelf={{ base: 'flex-end', lg: 'auto' }}
          alt="food guide"
          src="/food_guide.png"
          w={234}
          h={147}
          objectFit="contain"
        />
      </Flex>

      {/* Desktop View */}
      <Box display={{ base: 'none', lg: 'flex' }}>
        <HStack justifyContent="space-between" gap={8}>
          {reviews.map(({ signature, review }) => (
            <ReviewCard key={signature} review={review} signature={signature} />
          ))}
        </HStack>
      </Box>

      {/* Mobile View */}
      <Box
        display={{ base: 'flex', lg: 'none' }}
        alignItems="center"
        ref={containerRef}
      >
        <ReviewCard {...reviews[activeIndex]} />

        <HStack justifyContent="center" mt={4} gap={2}>
          {reviews.map((_, index) => (
            <Box
              key={index}
              w={2}
              h={2}
              rounded="full"
              bg={index === activeIndex ? 'secondary.500' : 'gray.300'}
            />
          ))}
        </HStack>
      </Box>
    </Box>
  );
};
